<template>
  <div class="resolved-state" :class="`resolved-state--${tone}`">
    <div class="resolved-state__icon" aria-hidden="true">
      <f7-icon :f7="icon" size="40"></f7-icon>
    </div>
    <h2 v-if="title" class="resolved-state__title">{{ title }}</h2>
    <p class="resolved-state__message">{{ message }}</p>
    <div v-if="showPill" class="resolved-state__pill">
      <turn-pill variant="resolved"></turn-pill>
    </div>
    <f7-button
      fill
      large
      class="resolved-state__done"
      @click="$emit('done')"
    >
      {{ t('general.done') }}
    </f7-button>
  </div>
</template>

<script setup>
/**
 * ResolvedState — the calm end-state for the "already handled" race: the leader
 * resolved the item, or the round moved on, and there is nothing left to answer
 * (resolvedElsewhere copy, `tone: 'calm'`, no pill — not an error).
 * Entrance is a gentle scale/fade, disabled under prefers-reduced-motion.
 *
 * IT NO LONGER CONFIRMS HER OWN ANSWER. The submit-step redesign (2026-08-14)
 * made the submit button the moment of confirmation, so the card flips in place
 * and confirms inline — crossing to a screen that replaced the card also threw
 * away the refreshed timeline carrying her own `you-replied` step.
 *
 * MARKED FOR THE CLEANUP PASS, not removed inline: `showPill` (with its
 * `TurnPill` import and `.resolved-state__pill` rule) and the `tone: 'ok'`
 * variant were the member's-own-resolution half and now have zero callers —
 * `IssueDecisionPage` mounts this only with `tone="calm"`.
 */
import { useI18n } from 'vue-i18n';
import TurnPill from '@/components/collection/TurnPill.vue';

defineProps({
  // 'ok' (green, member's own rescue) | 'calm' (neutral, already-handled)
  tone: {
    type: String,
    default: 'ok',
    validator: (v) => ['ok', 'calm'].includes(v),
  },
  icon: { type: String, default: 'checkmark_seal_fill' },
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  showPill: { type: Boolean, default: false },
});

defineEmits(['done']);

const { t } = useI18n({ useScope: 'global' });
</script>

<style scoped>
.resolved-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 20px 20px;
}

.resolved-state__icon {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  margin-bottom: 16px;
  animation: resolved-pop 420ms var(--poolit-transition-ease, cubic-bezier(0.4, 0, 0.2, 1)) both;
}

.resolved-state--ok .resolved-state__icon {
  background: var(--ci-ok-bg, #e1f3e8);
  color: var(--ci-ok-fg, #16964f);
}

.resolved-state--calm .resolved-state__icon {
  background: var(--ci-await-bg, #fbefd4);
  color: var(--ci-await-fg, #b45309);
}

.resolved-state__title {
  margin: 0 0 8px;
  font-size: var(--poolit-font-size-h3, 1.2rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--poolit-text-primary, #15201c);
  animation: resolved-rise 460ms 60ms var(--poolit-transition-ease, ease) both;
}

.resolved-state__message {
  margin: 0;
  max-width: 34ch;
  font-size: var(--poolit-font-size-body, 1rem);
  line-height: 1.65;
  color: var(--poolit-text-secondary, #3d4a44);
  font-weight: var(--poolit-font-weight-medium, 500);
  animation: resolved-rise 460ms 100ms var(--poolit-transition-ease, ease) both;
}

.resolved-state__pill {
  margin-top: 16px;
  animation: resolved-rise 460ms 160ms var(--poolit-transition-ease, ease) both;
}

.resolved-state__done {
  margin-top: 28px;
  min-width: 200px;
}

@keyframes resolved-pop {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.06);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes resolved-rise {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Respect reduced-motion: cross-fade only, no scale/translate. */
@media (prefers-reduced-motion: reduce) {
  .resolved-state__icon,
  .resolved-state__title,
  .resolved-state__message,
  .resolved-state__pill {
    animation: resolved-fade 200ms ease both;
  }
}

@keyframes resolved-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
